import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Search, AlertTriangle, Edit2, Wrench, ArrowUpDown, ArrowUp, ArrowDown, XCircle, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { useInventoryStock, useUpdateReorderLevel, useRecordTransaction } from '@/hooks/useInventory';
import type { InventoryStock } from '@/types/database/inventory';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CATEGORY_TYPES, CATEGORY_TYPE_COLORS, CategoryType } from '@/constants/categoryTypes';
import { useAuth } from '@/contexts/AuthContext';

type StockHealth = 'out-of-stock' | 'critical' | 'warning' | 'healthy';

function getStockHealth(item: InventoryStock): StockHealth {
  if (item.current_quantity <= 0) return 'out-of-stock';
  if (item.reorder_level > 0 && item.current_quantity <= item.reorder_level) return 'critical';
  if (item.reorder_level > 0 && item.current_quantity <= item.reorder_level * 1.5) return 'warning';
  return 'healthy';
}

const HEALTH_CONFIG: Record<StockHealth, { label: string; variant: 'destructive' | 'secondary' | 'outline' | 'default'; className: string; icon: typeof AlertTriangle | null; progressColor: string }> = {
  'out-of-stock': { label: 'Out of Stock', variant: 'destructive', className: '', icon: XCircle, progressColor: 'bg-destructive' },
  'critical': { label: 'Critical', variant: 'destructive', className: 'bg-harvest-warning text-white border-harvest-warning', icon: AlertTriangle, progressColor: 'bg-harvest-warning' },
  'warning': { label: 'Warning', variant: 'outline', className: 'border-harvest-warning text-harvest-warning', icon: ShieldAlert, progressColor: 'bg-harvest-warning' },
  'healthy': { label: 'Healthy', variant: 'outline', className: 'border-primary text-primary', icon: CheckCircle2, progressColor: 'bg-primary' },
};

export function InventoryStockTable() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [categoryTypeFilter, setCategoryTypeFilter] = useState<CategoryType | 'all'>('all');
  const [editingStock, setEditingStock] = useState<InventoryStock | null>(null);
  const [newReorderLevel, setNewReorderLevel] = useState('');
  const [adjustingStock, setAdjustingStock] = useState<InventoryStock | null>(null);
  const [adjustType, setAdjustType] = useState<'add' | 'remove'>('add');
  const [adjustQuantity, setAdjustQuantity] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const { userRole } = useAuth();
  const isAdmin = userRole === 'admin';

  const { data: stock, isLoading } = useInventoryStock(
    categoryTypeFilter !== 'all' ? { categoryType: categoryTypeFilter } : undefined
  );
  const updateReorderLevel = useUpdateReorderLevel();
  const recordTransaction = useRecordTransaction();

  const filteredStock = stock?.filter((item) =>
    item.product?.name?.toLowerCase().includes(search.toLowerCase())
  );

  const toggleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ column }: { column: string }) => {
    if (sortKey !== column) return <ArrowUpDown className="ml-1 h-3 w-3 inline opacity-40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3 inline" />
      : <ArrowDown className="ml-1 h-3 w-3 inline" />;
  };

  const getSortValue = (item: InventoryStock, key: string): string | number => {
    switch (key) {
      case 'product': return item.product?.name?.toLowerCase() || '';
      case 'category': return getCategoryName(item)?.toLowerCase() || '';
      case 'type': return getCategoryType(item)?.toLowerCase() || '';
      case 'stock': return item.current_quantity;
      case 'reorder': return item.reorder_level;
      case 'status': {
        const h = getStockHealth(item);
        return h === 'out-of-stock' ? 0 : h === 'critical' ? 1 : h === 'warning' ? 2 : 3;
      }
      default: return '';
    }
  };

  const sortedStock = (() => {
    if (!filteredStock || !sortKey) return filteredStock;
    return [...filteredStock].sort((a, b) => {
      const aVal = getSortValue(a, sortKey);
      const bVal = getSortValue(b, sortKey);
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  })();

  const handleEditReorderLevel = (item: InventoryStock) => {
    setEditingStock(item);
    setNewReorderLevel(item.reorder_level.toString());
  };

  const handleSaveReorderLevel = () => {
    if (editingStock) {
      updateReorderLevel.mutate({
        productId: editingStock.product_id,
        reorderLevel: parseFloat(newReorderLevel) || 0,
      });
      setEditingStock(null);
    }
  };

  const handleAdjustStock = (item: InventoryStock) => {
    setAdjustingStock(item);
    setAdjustType('add');
    setAdjustQuantity('');
    setAdjustNotes('');
  };

  const handleSaveAdjustment = async () => {
    if (!adjustingStock || !adjustQuantity) return;
    const qty = parseFloat(adjustQuantity);
    if (!qty || qty <= 0) return;

    await recordTransaction.mutateAsync({
      product_id: adjustingStock.product_id,
      transaction_type: 'adjustment',
      quantity: adjustType === 'remove' ? -qty : qty,
      reference_type: 'manual',
      reference_number: `ADJ-${new Date().toISOString().split('T')[0]}`,
      notes: `${adjustType === 'add' ? 'Stock added' : 'Stock removed'}${adjustNotes ? `: ${adjustNotes}` : ''}`,
    });

    setAdjustingStock(null);
  };

  const getCategoryType = (item: InventoryStock): CategoryType | null => {
    const product = item.product as any;
    return product?.category?.category_type || null;
  };

  const getCategoryName = (item: InventoryStock): string | null => {
    const product = item.product as any;
    return product?.category?.name || null;
  };

  const getStockBarValue = (item: InventoryStock): number => {
    if (item.reorder_level <= 0) return 100;
    const ratio = (item.current_quantity / (item.reorder_level * 2)) * 100;
    return Math.min(Math.max(ratio, 0), 100);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search products..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select 
          value={categoryTypeFilter} 
          onValueChange={(value) => setCategoryTypeFilter(value as CategoryType | 'all')}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {CATEGORY_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {type}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <TooltipProvider>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('product')}>Product <SortIcon column="product" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('category')}>Category <SortIcon column="category" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('type')}>Type <SortIcon column="type" /></TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('stock')}>Stock <SortIcon column="stock" /></TableHead>
                <TableHead className="text-right cursor-pointer select-none" onClick={() => toggleSort('reorder')}>Reorder Level <SortIcon column="reorder" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('status')}>Status <SortIcon column="status" /></TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedStock?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    {categoryTypeFilter !== 'all' 
                      ? `No ${categoryTypeFilter} products found in inventory`
                      : 'No inventory data found'
                    }
                  </TableCell>
                </TableRow>
              ) : (
                sortedStock?.map((item) => {
                  const categoryType = getCategoryType(item);
                  const categoryName = getCategoryName(item);
                  const health = getStockHealth(item);
                  const config = HEALTH_CONFIG[health];
                  const barValue = getStockBarValue(item);
                  
                  return (
                    <TableRow key={item.id} className={health === 'out-of-stock' || health === 'critical' ? 'bg-destructive/5' : ''}>
                      <TableCell 
                        className="font-medium cursor-pointer hover:underline text-primary"
                        onClick={() => navigate(`/inventory/product/${item.product_id}`)}
                      >
                        {item.product?.name || 'Unknown Product'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {categoryName || '-'}
                      </TableCell>
                      <TableCell>
                        {categoryType ? (
                          <Badge variant="outline" className={CATEGORY_TYPE_COLORS[categoryType]}>
                            {categoryType}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="space-y-1.5">
                              <span className="font-mono font-medium">
                                {item.current_quantity.toLocaleString()} {item.unit_of_measure}
                              </span>
                              <div className="relative h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                                <div 
                                  className={`h-full rounded-full transition-all ${config.progressColor}`}
                                  style={{ width: `${barValue}%` }}
                                />
                              </div>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Last updated: {format(new Date(item.last_updated), 'MMM d, yyyy HH:mm')}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="text-right font-mono text-muted-foreground">
                        {item.reorder_level.toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={config.variant} 
                          className={`gap-1 ${config.className}`}
                        >
                          {config.icon && <config.icon className="h-3 w-3" />}
                          {config.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditReorderLevel(item)}
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleAdjustStock(item)}
                            title="Adjust stock"
                          >
                            <Wrench className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </TooltipProvider>

      {/* Edit Reorder Level Dialog */}
      <Dialog open={!!editingStock} onOpenChange={() => setEditingStock(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Reorder Level</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <p className="text-sm text-muted-foreground">
                {editingStock?.product?.name}
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reorder-level">Reorder Level</Label>
              <Input
                id="reorder-level"
                type="number"
                value={newReorderLevel}
                onChange={(e) => setNewReorderLevel(e.target.value)}
                min="0"
                step="0.01"
              />
              <p className="text-xs text-muted-foreground">
                You'll be alerted when stock falls below this level
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingStock(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveReorderLevel} disabled={updateReorderLevel.isPending}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Adjust Stock Dialog */}
      <Dialog open={!!adjustingStock} onOpenChange={() => setAdjustingStock(null)}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Adjust Stock</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <p className="text-sm text-muted-foreground">
                {adjustingStock?.product?.name}
              </p>
              <p className="text-xs text-muted-foreground">
                Current stock: {adjustingStock?.current_quantity.toLocaleString()} {adjustingStock?.unit_of_measure}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Adjustment Type</Label>
              <Select value={adjustType} onValueChange={(v) => setAdjustType(v as 'add' | 'remove')}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add Stock (+)</SelectItem>
                  <SelectItem value="remove">Remove Stock (-)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-quantity">Quantity</Label>
              <Input
                id="adjust-quantity"
                type="number"
                value={adjustQuantity}
                onChange={(e) => setAdjustQuantity(e.target.value)}
                min="0.01"
                step="0.01"
                placeholder="Enter quantity"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjust-notes">Notes (Optional)</Label>
              <Textarea
                id="adjust-notes"
                value={adjustNotes}
                onChange={(e) => setAdjustNotes(e.target.value)}
                placeholder="Reason for adjustment..."
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustingStock(null)}>
              Cancel
            </Button>
            <Button onClick={handleSaveAdjustment} disabled={recordTransaction.isPending}>
              {recordTransaction.isPending ? 'Saving...' : 'Save Adjustment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
